import type { AuthDto } from '@mathflat/domain/@entities/Auth'
import { jwtTokenService } from '@mathflat/domain/@entities/Auth/jwtTokenService'
import { makeAutoObservable } from 'mobx'
import type { AuthRoute } from 'src/router'

import authApi from '@/api/auth.api'
import { academyController } from '@/controller/academy.controller'
import { authController } from '@/controller/auth.controller'
import { CommonError } from '@/dto'
import type FindPassword from '@/page/etc/Login/_dto/FindPassword'
import { envUtils } from '@/utils'
import { getXPlatform } from '@/utils/axios/maxios'
import { buildLoginRedirectSearchParams } from '@/utils/loginRedirect'

import {
  notifyElectronLoginFromJwtLoginResponse,
  notifyElectronLogout,
} from './electronAuthBridge.service'
import { localStorageService } from './storage.service'

class AuthService {
  isKeepSignedInChecked = jwtTokenService.isUsingLocalStorage

  constructor() {
    makeAutoObservable(this)
  }

  async finalizeJwtLogin(
    loginResponse: Pick<AuthDto.LoginResponseDto, 'accessToken' | 'refreshToken' | 'userId'>,
  ) {
    await jwtTokenService.processJwtLoginResponse(loginResponse)
    notifyElectronLoginFromJwtLoginResponse(loginResponse)
  }

  async initializeJwtAuth() {
    // 1. axios 인스턴스 초기화 (env + platform 한 번에)
    const environment = envUtils.isLive
      ? 'live'
      : envUtils.isStaging
        ? 'stg'
        : localStorageService.getItem('DEV_API') ?? 'dev'

    const isNamedEnv = environment === 'live' || environment === 'stg' || environment === 'dev'
    jwtTokenService.init({
      env: isNamedEnv ? environment : 'custom',
      customURL: isNamedEnv ? undefined : environment,
      platform: getXPlatform(),
    })

    // 3. JWT 토큰 서비스 이벤트 핸들러 설정
    const redirectToLoginPath = () => {
      const loginPath = localStorageService.getItem('LOGIN_PATH') ?? '/login'
      const { pathname, search } = window.location
      if (pathname === loginPath) return

      // search 만 이어붙이면 원래 pathname 이 유실되므로 (예: /login?redirectUri=...),
      // 현재 위치 전체를 redirectPathAfterLogin 으로 감싸 로그인 후 복귀할 수 있게 한다.
      window.location.replace(
        `${loginPath}?${buildLoginRedirectSearchParams(pathname, search).toString()}`,
      )
    }

    jwtTokenService.setEventHandlers({
      onTokenCleared: () => {
        redirectToLoginPath()
      },
      onTokenNotFound: () => {
        redirectToLoginPath()
      },
      onTokenReceived: () => {
        window.location.reload()
      },
    })

    // 4. 레거시 토큰 마이그레이션 (jwtTokenService가 알아서 처리)
    await jwtTokenService.initializeAuth()
  }

  // PAY_DENY 고객 로그인시 상담 제안
  async postSubscriptionOffer(...data: Parameters<typeof academyController.postSubscriptionOffer>) {
    return academyController.postSubscriptionOffer(...data)
  }

  async logout() {
    const legacyToken = jwtTokenService.getLegacyToken()
    const refreshToken = jwtTokenService.getRefreshToken()

    const logoutPromises: Promise<unknown>[] = []
    if (legacyToken) {
      logoutPromises.push(authApi.postLegacyLogout({ legacyToken }))
    }
    if (refreshToken) {
      logoutPromises.push(authApi.postLogout({ refreshToken }))
    }

    // API 요청이 완료될 때까지 대기 (실패해도 계속 진행)
    await Promise.allSettled(logoutPromises)

    jwtTokenService.clearTokens()
    notifyElectronLogout()
  }

  async loginByJwt(loginRequest: AuthDto.LoginRequestDto) {
    // 로그인 상태 유지가 체크되어 있으면 localStorage 사용 설정
    if (this.isKeepSignedInChecked) {
      jwtTokenService.useLocalStorage()
    }

    const { data } = await authApi.postLogin(loginRequest)
    await this.finalizeJwtLogin(data)

    return data
  }

  async loginByNaverWorks(code: string) {
    const { data } = await authApi.postNaverWorksLogin({
      code,
      userType: 'TEACHER',
      serviceType: 'MATHFLAT',
    })
    await this.finalizeJwtLogin(data)

    return data
  }

  async loginByAiep(code: string) {
    const { data } = await authApi.postAiepLogin({ code, userType: 'TEACHER' })

    // accessToken 없는 응답이 finalizeJwtLogin → syncLegacyTokenFromJwt 의 빈 Bearer 호출 → 401
    // → clearTokens() 경로로 흘러 정상 세션 토큰까지 삭제되는 것을 막는다.
    if (!data?.accessToken) {
      throw new CommonError('AIEP 로그인 응답이 유효하지 않습니다.', { type: 'client' })
    }

    // SSO 오설정으로 학생 응답이 선생님 진입으로 흘러 TEACHER_ID 에 학생 ID 가 박히는 사고 방지.
    if (data.userType !== 'TEACHER') {
      throw new CommonError('선생님 계정으로 로그인해주세요.', { type: 'client' })
    }

    await this.finalizeJwtLogin(data)

    return data
  }

  saveLoginResponseToStorage(
    { userId, academyId }: Pick<AuthDto.LoginResponseDto, 'academyId' | 'userId'>,
    loginPath?: AuthRoute,
    loginId?: string,
  ) {
    // TODO: v1 코드
    localStorageService.setItem('TEACHER_ID')(userId)
    localStorageService.setItem('ACADEMY_ID')(academyId)

    // v2 코드
    localStorageService.setItem('teacher-id')(userId)

    if (loginPath) {
      localStorageService.setItem('LOGIN_PATH')(loginPath)
    }

    if (loginId) {
      localStorageService.setItem('SAVED_LOGIN_ID')(loginId)
    }
  }

  async findPasswordFirstStep(account: FindPassword['account']) {
    await authController.findPasswordFirstStep(account)
  }

  async findPasswordSecondStep(token: FindPassword['resetPassword']['token']) {
    await authController.findPasswordSecondStep({ token })
  }

  async findPasswordThirdStep(passwordField: FindPassword['resetPassword']) {
    await authController.findPasswordThirdStep(passwordField)
  }
}

export const authService = new AuthService()
